(() => {
    'use strict';

    // Chart.js paints ticks, legends and grid lines in a fixed grey for a white page. Ticks and legends take
    // the hint colour, which the dark scheme flips, and grid lines a translucent grey that reads on either
    // ground. The charts already drawn are redrawn here when the scheme changes, whether by the system or
    // by the appearance switch. A legend resolves its colour against the defaults on every update, but a
    // scale copies the default into its own options when the chart is built, so the copy is swapped by hand
    // wherever it still holds the colour that was the default. A colour a chart chose for itself is left.
    const applyColorScheme = () => {
        const previousColor = Chart.defaults.color;
        const hintColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--howspace-color-form-hint')
            .trim();
        if (hintColor !== '') {
            Chart.defaults.color = hintColor;
        }
        Chart.defaults.borderColor = 'rgba(128, 128, 128, .25)';

        Object.values(Chart.instances).forEach(chart => {
            Object.values(chart.config.options.scales || {}).forEach(scale => {
                [scale.ticks, scale.title].forEach(part => {
                    if (part && part.color === previousColor) {
                        part.color = Chart.defaults.color;
                    }
                });
            });
            chart.update('none');
        });
    };

    howspace.chart = {
        COLOR_SCHEME_CHANGED: 'color-scheme:changed',
        chartReady : false,
        init: async (readyFn) => {
            if (howspace.chart.chartReady) {
                readyFn();
                return;
            }

            await lib('chart.js', 'chart.js');

            Chart.defaults.font.family = getComputedStyle(document.documentElement).getPropertyValue('--howspace-content-font-family');
            Chart.defaults.font.size = 14;

            applyColorScheme();
            real.events.on(howspace.chart.COLOR_SCHEME_CHANGED, applyColorScheme);
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyColorScheme);

            howspace.chart.chartReady = true;
            readyFn();
        },
        registerCenteringPlugin: () => {
            Chart.register({
                id: 'centerText',
                beforeDraw(chart) {
                    if (chart.config.options.elements?.center) {
                        // Get ctx from string
                        const ctx = chart.ctx;

                        // Get options from the center object in options
                        const centerConfig = chart.config.options.elements.center;
                        const fontStyle = centerConfig.fontStyle || 'GT Walsheim';
                        const txt = centerConfig.text;
                        const subtxt = centerConfig.subtext;
                        const color = centerConfig.color || '#000';
                        const subcolor = centerConfig.subcolor || '#000';
                        const maxFontSize = centerConfig.maxFontSize || 38;
                        const sidePadding = centerConfig.sidePadding || 20;
                        const sidePaddingCalculated = (sidePadding / 100) * (chart._metasets[0].data[0].innerRadius * 2);

                        // Start with a base font of 30px
                        ctx.font = '30px ' + fontStyle;

                        // Get the width of the string and also the width of the element minus padding
                        const stringWidth = ctx.measureText(txt).width;
                        const elementWidth = (chart._metasets[0].data[0].innerRadius * 2) - sidePaddingCalculated;

                        // Find out how much the font can grow in width
                        const widthRatio = elementWidth / stringWidth;
                        const newFontSize = Math.floor(30 * widthRatio);
                        const elementHeight = (chart._metasets[0].data[0].innerRadius * 2);

                        // Pick a new font size so it will not be larger than the height of label
                        const fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);

                        // Set font settings to draw it correctly
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';

                        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
                        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

                        ctx.font = '700 ' + fontSizeToUse + 'px ' + fontStyle;
                        ctx.fillStyle = color;
                        ctx.fillText(txt, centerX, centerY);

                        // Set font settings to draw subtext correctly
                        ctx.font = '14px ' + fontStyle;
                        ctx.fillStyle = subcolor;
                        ctx.fillText(subtxt, centerX, centerY + (fontSizeToUse / 1.2));
                    }
                }
            });
        }
    };
})();
